import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { FormattedMessage } from 'react-intl';
import makeStyles from '@mui/styles/makeStyles';

// Package offer model — "Buy 4, Get 1 Free": pay for 4 seats, the 5th is a
// single bonus seat. The bonus is capped at 1 (it does not repeat every 5),
// so 10 seats = "10 for the price of 9", not "for the price of 8".
export const PACKAGE_SIZE = 5;
const PAID_PER_PACKAGE = 4;
const MAX_BONUS_SEATS = 1;

const useStyles = makeStyles(theme => ({
  nudgeMessage: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontWeight: 600,
    fontSize: 14,
    color: '#22283c',
    whiteSpace: 'nowrap',
  },
  giftIcon: {
    fontSize: 18,
    lineHeight: 1,
  },
  claimButton: {
    background: 'linear-gradient(135deg, #ff9a1f 0%, #ff7a00 100%)',
    color: '#ffffff',
    fontWeight: 700,
    fontSize: 12,
    borderRadius: 6,
    padding: theme.spacing(0.75, 1.5),
    textTransform: 'none',
    boxShadow: '0 2px 0 #b85100, 0 3px 8px rgba(255,122,0,.3)',
    minWidth: 'auto',
    '&:hover': {
      background: 'linear-gradient(135deg, #ff9a1f 0%, #ff7a00 100%)',
      boxShadow: '0 1px 0 #b85100, 0 4px 10px rgba(255,122,0,.35)',
    },
  },
  confirmation: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontWeight: 600,
    fontSize: 14,
    color: '#0E7A3C',
    whiteSpace: 'nowrap',
  },
}));

/**
 * Threshold-triggered treatment for the "Buy 4, Get 1 Free" package offer,
 * rendered next to the seat stepper. Purely presentational — driven by the
 * seat count, NOT by the backend per-seat discount (a discounted seat is not
 * the same as a free/bonus seat).
 *  - one step away from the package (4 seats): nudge + one-click "Claim it"
 *  - bonus unlocked (5+ seats): inline "bonus seat applied" confirmation
 *  - otherwise: nothing (no nudge/badge below the threshold)
 */
export const BonusSeatTreatment = (
  { seats, isAnnual, hasSubscription, setSeats }: {
    seats: number;
    isAnnual: boolean;
    hasSubscription: boolean;
    setSeats: (seats: number) => void;
  },
) => {
  const classes = useStyles();

  // Offer is annual-only and targets new purchases.
  if (!isAnnual || hasSubscription) {
    return null;
  }

  const bonusSeats = seats >= PACKAGE_SIZE ? MAX_BONUS_SEATS : 0;

  // Bonus seat unlocked — confirm it so the unchanged total reads as a win.
  if (bonusSeats >= 1) {
    const paidSeats = seats - bonusSeats;
    return (
      <Box id="bonus-seat-confirmation" className={classes.confirmation}>
        <span className={classes.giftIcon}>🎉</span>
        <Typography component="span" className={classes.confirmation}>
          <FormattedMessage
            id="page.plan.v2.bonus.applied"
            values={{
              total: seats,
              paid: paidSeats,
              b: (chunks: string) => <strong>{chunks}</strong>,
            }}
          />
        </Typography>
      </Box>
    );
  }

  // One step away from unlocking the bonus seat — nudge + one-click claim.
  if (seats !== PAID_PER_PACKAGE) {
    return null;
  }

  return (
    <Box id="bonus-seat-nudge" display="flex" alignItems="center" gap={1.5}>
      <Typography component="span" className={classes.nudgeMessage}>
        <span className={classes.giftIcon}>🎁</span>
        <FormattedMessage
          id="page.plan.v2.bonus.nudge"
          values={{ b: (chunks: string) => <strong>{chunks}</strong> }}
        />
      </Typography>
      <Button
        id="bonus-seat-claim-button"
        className={classes.claimButton}
        onClick={() => setSeats(PACKAGE_SIZE)}
      >
        <FormattedMessage id="page.plan.v2.bonus.claim" />
      </Button>
    </Box>
  );
};
